/**
 * resolveEntryRedirect — pure routing-decision helper for the App entry points.
 *
 * Centralises redirects that were previously inlined in App.js so they can be
 * unit-tested without mounting React.
 *
 * Decision table:
 *
 * pathname === '/'
 *   flow === 'login_plugin'                               → 'loading'  (render <LoadingPage/>)
 *   plugin + mode === 'embedded'                          → '/embedded/app'
 *   cloud + !authorizationFetched                         → '/signin'
 *   cloud + authorizationFetched
 *     && (initURL === '' || '/' || '/signin')             → '/app'
 *   cloud + authorizationFetched                          → initURL
 *
 * pathname in ['/signin', '/signin/embedded']
 *   plugin + mode === 'embedded'                          → '/embedded/app'
 *   sessionToken present                                  → '/app'
 *   (else)                                                → null (stay on sign-in page)
 *
 * All other pathnames                                     → null (no early redirect)
 *
 * @param {object} params
 * @param {string} params.pathname
 * @param {string|undefined} params.mode            - URL query param ?mode=...
 * @param {string|undefined} params.flow            - URL query param ?flow=...
 * @param {'cloud'|'plugin'} params.productId        - selected transport profile
 * @param {boolean} params.authorizationFetched     - whether auth.authorization.isFetched
 * @param {string} params.initURL                   - auth.initURL from redux store
 * @param {string|null|undefined} params.sessionToken
 * @returns {string|null} Redirect target, 'loading', or null.
 */
export function resolveEntryRedirect({
  pathname,
  mode,
  flow,
  productId = 'cloud',
  authorizationFetched,
  initURL,
  sessionToken,
}) {
  if (pathname === '/') {
    if (flow === 'login_plugin') return 'loading';
    if (mode === 'embedded' && productId === 'plugin') return '/embedded/app';
    if (!authorizationFetched) return '/signin';
    if (!initURL || initURL === '/' || initURL === '/signin') return '/app';
    return initURL;
  }

  if (pathname === '/signin' || pathname === '/signin/embedded') {
    if (mode === 'embedded' && productId === 'plugin') return '/embedded/app';
    if (sessionToken) return '/app';
  }

  return null;
}
